import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from './hooks/useToast';
import { useWallet } from './WalletContext';
import './GameEntry.css';

function FullSangamGame({ gameName, onBack }) {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { walletBalance, updateWalletBalance } = useWallet();
  const dropdownOpenRef = useRef(null);
  const dropdownCloseRef = useRef(null);
  const [availableDigits, setAvailableDigits] = useState([]);
  const [openPannaList, setOpenPannaList] = useState([]);
  const [closePannaList, setClosePannaList] = useState([]);
  const [openPanna, setOpenPanna] = useState('');
  const [closePanna, setClosePanna] = useState('');
  const [points, setPoints] = useState('');
  const [showOpenDropdown, setShowOpenDropdown] = useState(false);
  const [showCloseDropdown, setShowCloseDropdown] = useState(false);
  const [openSearchTerm, setOpenSearchTerm] = useState('');
  const [closeSearchTerm, setCloseSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openTime = location.state?.openTime || '09:00';
  const closeTime = location.state?.closeTime || '18:00';
  const gameId = location.state?.gameId || '';
  const gameType = location.state?.gameTypeId || '';
  const gameNameFromState = location.state?.gameName || '';
  const gameTypeLabel = 'Full Sangam';

  // Fetch game data from API
  useEffect(() => {
    const fetchGameData = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://backend.mainbazar777.site/api/game-ank');
        const data = await response.json();

        if (data.game_ank && data.game_ank.length > 0) {
          const gameData = data.game_ank[0];
          setAvailableDigits(gameData);

          // Parse full_sangam_open_ank and full_sangam_close_ank
          let openList = [];
          let closeList = [];

          if (gameData.full_sangam_open_ank) {
            try {
              openList = typeof gameData.full_sangam_open_ank === 'string'
                ? JSON.parse(gameData.full_sangam_open_ank)
                : gameData.full_sangam_open_ank;
            } catch (e) {
              console.error('Error parsing open ank:', e);
            }
          }

          if (gameData.full_sangam_close_ank) {
            try {
              closeList = typeof gameData.full_sangam_close_ank === 'string'
                ? JSON.parse(gameData.full_sangam_close_ank)
                : gameData.full_sangam_close_ank;
            } catch (e) {
              console.error('Error parsing close ank:', e);
            }
          }

          setOpenPannaList(openList);
          setClosePannaList(closeList);
        }
        setError('');
      } catch (err) {
        setError('Failed to load game data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchGameData();
  }, []);

  // Filter digits based on search input
  useEffect(() => {
    if (!openPanna.trim() || !closePanna.trim()) {
      return;
    }
  }, [openPanna, closePanna]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpenRef.current && !dropdownOpenRef.current.contains(event.target)) {
        setShowOpenDropdown(false);
      }
      if (dropdownCloseRef.current && !dropdownCloseRef.current.contains(event.target)) {
        setShowCloseDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-navigate home when Full Sangam bid window closes (5 min before open time)
  useEffect(() => {
    if (isOpenBidClosed()) {
      navigate('/');
      return;
    }
    const kt = getKolkata();
    const current = toMarketMinutes(kt.getHours(), kt.getMinutes());
    const secs = kt.getSeconds();
    const [h, m] = openTime.split(':').map(Number);
    const msUntilClose = ((toMarketMinutes(h, m) - 5) - current) * 60000 - secs * 1000;
    const t = setTimeout(() => navigate('/'), msUntilClose);
    return () => clearTimeout(t);
  }, [openTime, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter open panna list based on search term
  const filteredOpenList = openPannaList.filter(item =>
    item.includes(openSearchTerm)
  );

  // Filter close panna list based on search term
  const filteredCloseList = closePannaList.filter(item =>
    item.includes(closeSearchTerm)
  );

  const handlePlaceBid = async () => {
    if (!openPanna.trim() || !closePanna.trim() || !points.trim()) {
      toast.error('Please enter both pannas and points');
      return;
    }

    // Validate panna format (should be 3-digit numbers)
    if (!/^\d{3}$/.test(openPanna) || !/^\d{3}$/.test(closePanna)) {
      toast.error('Each panna must be a 3-digit number');
      return;
    }

    if (isOpenBidClosed()) {
      toast.error('Market Closed');
      if (navigator.vibrate) navigator.vibrate(200);
      navigate('/');
      return;
    }

    try {
      setIsSubmitting(true);
      const playerData = JSON.parse(localStorage.getItem('user') || '{}');
      const formData = new FormData();
      formData.append('game_id', gameId);
      formData.append('game_name', gameNameFromState);
      formData.append('game_type', gameType);
      formData.append('player_id', playerData.id || ''); 
      formData.append('session', '');
      formData.append('bid_amount', points);
      formData.append('bid_number', `${openPanna}${closePanna}`);

      const response = await fetch('https://backend.mainbazar777.site/api/play-game', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if(data.balance === false) {
        toast.error('Insufficient balance. Please add funds to your wallet.');
      } else if (response.ok && data.success) {
        toast.success(`Bid Placed! For Open Panna ${openPanna} and Close Panna ${closePanna}, Amount: ₹${points}`);
        
        // Refresh wallet balance after successful bid
        const playerData = JSON.parse(localStorage.getItem('user') || '{}');
        if (playerData?.id) {
          updateWalletBalance(playerData.id);
        }
        
        setOpenPanna('');
        setClosePanna('');
        setPoints('');
      } else {
        toast.error(data.message || 'Failed to place bid');
      }
    } catch (err) {
      console.error('Error placing bid:', err);
      toast.error('Error placing bid. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toMarketMinutes = (h, m) => ((h * 60 + m) - 120 + 1440) % 1440;

  const getKolkata = () => {
    const now = new Date();
    return new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  };

  // Full Sangam bids close 5 minutes before the open time
  const isOpenBidClosed = () => {
    const kt = getKolkata();
    const current = toMarketMinutes(kt.getHours(), kt.getMinutes());
    const [h, m] = openTime.split(':').map(Number);
    return current >= toMarketMinutes(h, m) - 5;
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${String(displayHour).padStart(2, '0')}:${minutes} ${ampm}`;
  };

  const today = new Date();
  const dateString = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;

  if (loading) {
    return (
      <div className="game-entry">
        <header className="game-entry-header">
          <button className="back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="game-title">{gameName || 'Loading...'}</h1>
          <div style={{ width: '24px' }}></div>
        </header>
        <div className="game-content">
          <div className="loading-message">Loading game data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="game-entry">
        <header className="game-entry-header">
          <button className="back-btn" onClick={onBack}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h1 className="game-title">{gameName || 'Error'}</h1>
          <div style={{ width: '24px' }}></div>
        </header>
        <div className="game-content">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="game-entry">
      <header className="game-entry-header">
        <button className="back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <h1 className="game-title">{gameName || gameTypeLabel}</h1>
        <button className="wallet-btn" onClick={() => navigate("/funds")}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="6" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
            <path d="M2 10H22" stroke="white" strokeWidth="2"/>
            <circle cx="18" cy="15" r="1.5" fill="white"/>
          </svg>
          <span className="wallet-balance">₹ {walletBalance}</span>
        </button>
      </header>

      <div className="game-content">
        <div className="game-info-section">
          <h2 className="game-type-heading">{gameTypeLabel}</h2>
          <div className="info-row">
            <div className="info-item">
              <label>Date</label>
              <input type="text" value={dateString} readOnly disabled />
            </div>
          </div>

          <div className="time-section">
            <div className="time-item">
              <span className="time-label">Time Open:</span>
              <span className="time-value">{formatTime(openTime)}</span>
            </div>
            <div className="time-item">
              <span className="time-label">Time Close:</span>
              <span className="time-value">{formatTime(closeTime)}</span>
            </div>
          </div>
        </div>

        <div className="input-section">
          <div className="input-row">
            <div className="input-item">
              <label>Open Panna:</label>
              <div className="dropdown-container" ref={dropdownOpenRef}>
                <input
                  type="text"
                  placeholder="Search or select"
                  value={showOpenDropdown ? openSearchTerm : openPanna}
                  onChange={(e) => {
                    if (showOpenDropdown) {
                      setOpenSearchTerm(e.target.value);
                    } else {
                      setOpenPanna(e.target.value);
                    }
                  }}
                  onFocus={() => {
                    setShowOpenDropdown(true);
                    setOpenSearchTerm('');
                  }}
                  onBlur={() => {
                    if (!openPanna && openSearchTerm) {
                      setOpenPanna(openSearchTerm);
                    }
                  }}
                  className="digit-input"
                />
                {showOpenDropdown && (
                  <div className="dropdown-list">
                    {filteredOpenList.length > 0 ? (
                      filteredOpenList.slice(0, 50).map((item, index) => (
                        <div
                          key={index}
                          className="dropdown-item"
                          onClick={() => {
                            setOpenPanna(item);
                            setOpenSearchTerm('');
                            setShowOpenDropdown(false);
                          }}
                        >
                          {item}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-item disabled">No results found</div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="input-item">
              <label>Close Panna:</label>
              <div className="dropdown-container" ref={dropdownCloseRef}>
                <input
                  type="text"
                  placeholder="Search or select"
                  value={showCloseDropdown ? closeSearchTerm : closePanna}
                  onChange={(e) => {
                    if (showCloseDropdown) {
                      setCloseSearchTerm(e.target.value);
                    } else {
                      setClosePanna(e.target.value);
                    }
                  }}
                  onFocus={() => {
                    setShowCloseDropdown(true);
                    setCloseSearchTerm('');
                  }}
                  onBlur={() => {
                    if (!closePanna && closeSearchTerm) {
                      setClosePanna(closeSearchTerm);
                    }
                  }}
                  className="digit-input"
                />
                {showCloseDropdown && (
                  <div className="dropdown-list">
                    {filteredCloseList.length > 0 ? (
                      filteredCloseList.slice(0, 50).map((item, index) => (
                        <div
                          key={index}
                          className="dropdown-item"
                          onClick={() => {
                            setClosePanna(item);
                            setCloseSearchTerm('');
                            setShowCloseDropdown(false);
                          }}
                        >
                          {item}
                        </div>
                      ))
                    ) : (
                      <div className="dropdown-item disabled">No results found</div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="input-item">
              <label>Enter Funds:</label>
              <input
                type="number"
                placeholder="Funds"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                className="points-input"
              />
            </div>
          </div>

          <button 
            className="add-btn" 
            onClick={handlePlaceBid}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Placing Bid...' : 'Place Bid'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default FullSangamGame;
